/**
 * Constants extracted from Segments.tsx during the Maintainability Sprint
 * (2026-04-19 Task 1.1). See
 * docs/superpowers/plans/2026-04-19-maintainability-sprint.md for context.
 *
 * Contains:
 *   - SegmentStyle   visual config shape (bg / text / border / iconBg)
 *   - SEGMENT_STYLES Record keyed by CanonicalSegmentKey
 *   - RFM_STYLES     Record keyed by RfmSegmentKey
 *   - DEFAULT_STYLE  fallback when a segment key has no registered style
 *   - TABS           page tab definitions (canonical / rfm / featured)
 *   - TabKey         union type derived from TABS
 *   - REGIONS        Senegalese region filter options
 *
 * All string values preserved byte-for-byte from the original Segments.tsx
 * to keep the French UI copy intact.
 */

import type {
  CanonicalSegmentKey,
  RfmSegmentKey,
} from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Segment visual config
// ---------------------------------------------------------------------------

export interface SegmentStyle {
  readonly bg: string;
  readonly text: string;
  readonly border: string;
  readonly iconBg: string;
}

export const SEGMENT_STYLES: Record<CanonicalSegmentKey, SegmentStyle> = {
  vip: {
    bg: "bg-amber-50",
    text: "text-amber-800",
    border: "border-amber-200",
    iconBg: "bg-amber-100",
  },
  new: {
    bg: "bg-emerald-50",
    text: "text-emerald-800",
    border: "border-emerald-200",
    iconBg: "bg-emerald-100",
  },
  active: {
    bg: "bg-blue-50",
    text: "text-blue-800",
    border: "border-blue-200",
    iconBg: "bg-blue-100",
  },
  wholesale: {
    bg: "bg-violet-50",
    text: "text-violet-800",
    border: "border-violet-200",
    iconBg: "bg-violet-100",
  },
  at_risk: {
    bg: "bg-orange-50",
    text: "text-orange-800",
    border: "border-orange-200",
    iconBg: "bg-orange-100",
  },
  churned: {
    bg: "bg-red-50",
    text: "text-red-800",
    border: "border-red-200",
    iconBg: "bg-red-100",
  },
  dormant: {
    bg: "bg-stone-50",
    text: "text-stone-700",
    border: "border-stone-200",
    iconBg: "bg-stone-100",
  },
  high_frequency: {
    bg: "bg-teal-50",
    text: "text-teal-800",
    border: "border-teal-200",
    iconBg: "bg-teal-100",
  },
  cod_only: {
    bg: "bg-lime-50",
    text: "text-lime-800",
    border: "border-lime-200",
    iconBg: "bg-lime-100",
  },
  mobile_money_only: {
    bg: "bg-cyan-50",
    text: "text-cyan-800",
    border: "border-cyan-200",
    iconBg: "bg-cyan-100",
  },
};

export const RFM_STYLES: Record<RfmSegmentKey, SegmentStyle> = {
  champions: {
    bg: "bg-yellow-50",
    text: "text-yellow-800",
    border: "border-yellow-200",
    iconBg: "bg-yellow-100",
  },
  fideles: {
    bg: "bg-amber-50",
    text: "text-amber-800",
    border: "border-amber-200",
    iconBg: "bg-amber-100",
  },
  potentiels_fideles: {
    bg: "bg-lime-50",
    text: "text-lime-800",
    border: "border-lime-200",
    iconBg: "bg-lime-100",
  },
  nouveaux: {
    bg: "bg-emerald-50",
    text: "text-emerald-800",
    border: "border-emerald-200",
    iconBg: "bg-emerald-100",
  },
  prometteurs: {
    bg: "bg-teal-50",
    text: "text-teal-800",
    border: "border-teal-200",
    iconBg: "bg-teal-100",
  },
  a_reconquerir: {
    bg: "bg-rose-50",
    text: "text-rose-800",
    border: "border-rose-200",
    iconBg: "bg-rose-100",
  },
  a_risque: {
    bg: "bg-orange-50",
    text: "text-orange-800",
    border: "border-orange-200",
    iconBg: "bg-orange-100",
  },
  presque_inactifs: {
    bg: "bg-stone-50",
    text: "text-stone-700",
    border: "border-stone-200",
    iconBg: "bg-stone-100",
  },
  a_surveiller: {
    bg: "bg-sky-50",
    text: "text-sky-800",
    border: "border-sky-200",
    iconBg: "bg-sky-100",
  },
  perdus: {
    bg: "bg-red-50",
    text: "text-red-800",
    border: "border-red-200",
    iconBg: "bg-red-100",
  },
  inactifs: {
    bg: "bg-gray-50",
    text: "text-gray-600",
    border: "border-gray-200",
    iconBg: "bg-gray-100",
  },
};

export const DEFAULT_STYLE: SegmentStyle = {
  bg: "bg-stone-50",
  text: "text-stone-600",
  border: "border-stone-200",
  iconBg: "bg-stone-100",
};

// ---------------------------------------------------------------------------
// Tabs config
// ---------------------------------------------------------------------------

export const TABS = [
  { key: "canonical", label: "Segments" },
  { key: "rfm", label: "RFM" },
  { key: "featured", label: "Personnalis\u00e9s" },
] as const;

export type TabKey = (typeof TABS)[number]["key"];

// ---------------------------------------------------------------------------
// Regions (Senegalese operation zones per CLAUDE.md)
// ---------------------------------------------------------------------------

export const REGIONS = [
  { value: "", label: "Toutes les r\u00e9gions" },
  { value: "Dakar", label: "Dakar" },
  { value: "Kaolack", label: "Kaolack" },
  { value: "Saint-Louis", label: "Saint-Louis" },
  { value: "Fatick", label: "Fatick" },
  { value: "Thies-Mbour", label: "Thi\u00e8s-Mbour" },
  { value: "Diourbel-Touba", label: "Diourbel-Touba" },
];
